import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export interface CartItem {
    id: string; // variantId
    productId: string;
    name: string;
    price: number;
    quantity: number;
    shopId: string;
    shopName: string;
    shopCity?: string;
    logo_url?: string;
    appliedCoupon?: string;
    discountPercent?: number;
    discountLimit?: number;
    promoPrice?: number; // Explicit fixed promo price (overrides discountPercent calculation)
    currency: string;
}

interface CartContextType {
    items: CartItem[];
    addToCart: (item: CartItem, forceClear?: boolean) => { success: boolean, error?: 'shop_mismatch' };
    removeFromCart: (variantId: string) => void;
    updateQuantity: (variantId: string, quantity: number) => void;
    clearCart: () => void;
    totalAmount: number;
    couponCode: string | null;
    applyCoupon: (code: string) => void;
    removeCoupon: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const location = useLocation();
    const [items, setItems] = useState<CartItem[]>(() => {
        const saved = localStorage.getItem('padel_cart');
        return saved ? JSON.parse(saved) : [];
    });

    // [NEW] Coupon Logic
    const [couponCode, setCouponCode] = useState<string | null>(() => {
        return localStorage.getItem('padel_cart_coupon');
    });

    useEffect(() => {
        localStorage.setItem('padel_cart', JSON.stringify(items));
    }, [items]);

    useEffect(() => {
        if (couponCode) {
            localStorage.setItem('padel_cart_coupon', couponCode);
        } else {
            localStorage.removeItem('padel_cart_coupon');
        }
    }, [couponCode]);

    // Initialize from URL (Reactive to navigation)
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const code = params.get('coupon');
        // Always sync with URL param (even if null to clear it)
        setCouponCode(code);
    }, [location.search]);

    const addToCart = (newItem: CartItem, forceClear: boolean = false): { success: boolean, error?: 'shop_mismatch' } => {
        console.log("[CartContext] addToCart:", newItem.name, "Price (expected cents):", newItem.price, "ForceClear:", forceClear);

        let currentItems = items;

        if (forceClear) {
            currentItems = [];
            setCouponCode(null);
            localStorage.removeItem('padel_cart_coupon'); // Immediate cleanup
        }

        const existingShopId = currentItems.length > 0 ? currentItems[0].shopId : null;

        if (existingShopId && existingShopId !== newItem.shopId) {
            return { success: false, error: 'shop_mismatch' };
        }

        setItems(prev => {
            // If forced clear, ignore prev and start with newItem (or empty then add)
            // Ideally we use the 'prev' from useState to be safe, but since we logic-checked 'currentItems' above...
            // Actually, better to stick to functional update pattern properly.

            let baseItems = forceClear ? [] : prev;

            // Re-check mismatch inside functional update? No, 'existingShopId' check above 
            // is technically based on render-state 'items', which might be stale in high-freq updates?
            // Unlikely in this UI flow (modal confirmation). 
            // So we just trust 'forceClear' means we want ONLY this new item.

            const existing = baseItems.find(i => i.id === newItem.id);
            if (existing) {
                return baseItems.map(i => i.id === newItem.id
                    ? {
                        ...i,
                        quantity: i.quantity + newItem.quantity,
                        appliedCoupon: newItem.appliedCoupon || i.appliedCoupon,
                        discountPercent: newItem.discountPercent || i.discountPercent,
                        discountLimit: newItem.discountLimit || i.discountLimit,
                        promoPrice: newItem.promoPrice || i.promoPrice
                    }
                    : i
                );
            }
            return [...baseItems, newItem];
        });
        return { success: true };
    };

    const removeFromCart = (variantId: string) => {
        setItems(prev => prev.filter(i => i.id !== variantId));
    };

    const updateQuantity = (variantId: string, quantity: number) => {
        setItems(prev => prev.map(i => i.id === variantId ? { ...i, quantity } : i));
    };

    const clearCart = () => {
        setItems([]);
        setCouponCode(null);
    };

    const applyCoupon = (code: string) => setCouponCode(code);
    const removeCoupon = () => setCouponCode(null);

    const totalAmount = items.reduce((sum, item) => {
        let itemTotal = item.price * item.quantity;
        if (item.discountPercent && item.discountPercent > 0) {
            // If discountLimit is undefined, assume unlimited?
            // Actually backend return `quantity_limit: 1`. So it should be defined.
            // But if undefined (legacy item), fallback to item.quantity (full discount)? 
            // Providing safe fallback:
            const qtyDiscounted = Math.min(item.quantity, item.discountLimit ?? item.quantity);
            const qtyRegular = item.quantity - qtyDiscounted;

            let discountPrice = 0;
            if (item.promoPrice) {
                discountPrice = item.promoPrice;
            } else {
                discountPrice = item.price * (1 - (item.discountPercent / 100));
            }
            itemTotal = (qtyDiscounted * discountPrice) + (qtyRegular * item.price);
        }
        return sum + itemTotal;
    }, 0);

    return (
        <CartContext.Provider value={{ items, addToCart, removeFromCart, updateQuantity, clearCart, totalAmount, couponCode, applyCoupon, removeCoupon }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) throw new Error('useCart must be used within a CartProvider');
    return context;
};
